import React, { useEffect, useState } from 'react';

import { Close as CloseIcon, Event as EventIcon } from '@mui/icons-material';
import {
  Button,
  Collapse,
  DialogActions,
  Divider,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Popover,
  Stack,
  Typography
} from '@mui/material';
import { LocalizationProvider, PickersDay, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { TransitionGroup } from 'react-transition-group';

import { sharedClasses } from './sharedClasses';
import { DARK_GREY } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';

import type { PickersActionBarProps, StaticDatePickerProps } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';

dayjs.extend(utc);
dayjs.extend(tz);

interface IControlledTimePicker extends Omit<StaticDatePickerProps<Dayjs>, 'onAccept'> {
  values: Date[] | Dayjs[];
  onAccept: (dates: Date[] | Dayjs[]) => void;
  label: string;
  required?: boolean;
  error?: string;
  timezone?: string;
}

interface IDayItem {
  day: Dayjs;
  removeSelectedDate: (item: Dayjs) => void;
}

const ServerDay = (props: any) => {
  const { selectedDays = [], onDaySelect, day, outsideCurrentMonth, timezone, ...other } = props;
  const isSelected = selectedDays
    .map((e: Dayjs) => e.format('M/D/YYYY'))
    .includes(day.format('M/D/YYYY'));
  const nextDayIsSelected = selectedDays
    .map((e: Dayjs) => e.format('M/D/YYYY'))
    .includes(day.add(1, 'day').format('M/D/YYYY'));
  const previousDayIsSelected = selectedDays
    .map((e: Dayjs) => e.format('M/D/YYYY'))
    .includes(day.subtract(1, 'day').format('M/D/YYYY'));

  const todayInTimezone = timezone ? dayjs().tz(timezone) : dayjs();
  const isToday = day.format('M/D/YYYY') === todayInTimezone.format('M/D/YYYY');

  const borderRadius = () => {
    if (previousDayIsSelected && nextDayIsSelected) {
      return 'unset';
    }
    if (nextDayIsSelected) {
      return '50% 0% 0% 50%';
    }
    if (previousDayIsSelected) {
      return '0% 50% 50% 0%';
    }
  };

  return (
    <PickersDay
      {...other}
      day={day}
      onDaySelect={onDaySelect}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={isSelected}
      today={isToday}
      sx={{
        ...sharedClasses.pickersDay,
        '&.Mui-selected': {
          background: `${theme.palette.action.main} !important`,
          borderRadius: borderRadius()
        }
      }}
    />
  );
};

const ActionBar = ({
  onAccept,
  onCancel
}: Omit<PickersActionBarProps, 'onClear' | 'onSetToday'>) => (
  <DialogActions>
    <Button
      onClick={onCancel}
      sx={{
        ...sharedClasses.genericRedButton,
        background: theme.palette.common.white,
        '&:hover': { background: theme.palette.common.white }
      }}
    >
      Cancel
    </Button>
    <Button onClick={onAccept} sx={sharedClasses.genericButton}>
      Submit
    </Button>
  </DialogActions>
);

const DayItem = ({ day, removeSelectedDate }: IDayItem) => (
  <Stack direction="row" justifyContent="space-between" mb={1}>
    <Typography sx={{ fontFamily: theme.typography.fontFamily }}>
      {dayjs(day).format('ddd, D MMMM YYYY')}
    </Typography>
    <IconButton
      onClick={() => removeSelectedDate(day)}
      sx={{ p: 0, color: theme.palette.error.main }}
    >
      <CloseIcon />
    </IconButton>
  </Stack>
);

const MultipleDatesPicker = ({
  values,
  onAccept,
  label,
  required,
  error,
  timezone,
  ...rest
}: IControlledTimePicker) => {
  const [selectedDays, setSelectedDays] = useState<Dayjs[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    setSelectedDays(values.map((v) => dayjs(v)));
  }, [values]);

  const onDaySelect = (day: any) => {
    // Preserve the visual calendar date by using year/month/day components
    const selectedDay = timezone
      ? dayjs.tz(`${day.year()}-${day.month() + 1}-${day.date()} 12:00`, timezone)
      : day;
    const dayKey = selectedDay.format('M/D/YYYY');

    const newValue = [...selectedDays];
    if (newValue.map((n) => n.format('M/D/YYYY')).includes(dayKey)) {
      setSelectedDays(newValue.filter((v) => v.format('M/D/YYYY') !== dayKey));
    } else {
      newValue.push(selectedDay);
      setSelectedDays(newValue.sort((a, b) => (a.isAfter(b) ? 1 : -1)));
    }
  };

  const onClose = () => {
    setSelectedDays(values.map((v) => dayjs(v)));
    setAnchorEl(null);
  };

  const onActionAccept = () => {
    onAccept(selectedDays.map((d) => d.toDate()));
    setAnchorEl(null);
  };

  const pickerDayComponent = (props: any) => (
    <ServerDay
      {...props}
      onDaySelect={onDaySelect}
      selectedDays={selectedDays}
      timezone={timezone}
    />
  );

  const actionBarComponent = () => <ActionBar onAccept={onActionAccept} onCancel={onClose} />;

  return (
    <Stack>
      <InputLabel sx={sharedClasses.multipleDatesPickerLabel}>
        {label}
        {required ? <Stack sx={{ color: theme.palette.error.main }}>*</Stack> : null}
      </InputLabel>
      <OutlinedInput
        error={selectedDays.length === 0}
        id="outlined-adornment-password"
        label="Password"
        sx={sharedClasses.multipleDatesPickerInput}
        value={selectedDays[0]?.format('DD/MM/YYYY') || ''}
        endAdornment={
          <InputAdornment position="end">
            {values.length > 1 && (
              <Stack sx={sharedClasses.multipleDatesPickerExtraDates}>{`+${
                values.length - 1
              }`}</Stack>
            )}
            <IconButton
              edge="end"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ '&:focus': { outline: 'none' } }}
            >
              <EventIcon />
            </IconButton>
          </InputAdornment>
        }
      />
      {!!error && (
        <Typography sx={{ ...sharedClasses.errorMessage, marginTop: '3px' }} variant="caption">
          {error}
        </Typography>
      )}
      <Popover
        anchorEl={anchorEl}
        onClose={onClose}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        sx={{
          '& .MuiPaper-root': {
            marginTop: 1,
            marginLeft: 1,
            display: 'flex'
          }
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDatePicker
            slots={{ day: pickerDayComponent, actionBar: actionBarComponent }}
            sx={sharedClasses.multipleDatesPickerStaticDatePicker}
            timezone={timezone}
            slotProps={{
              toolbar: { hidden: true }
            }}
            {...rest}
          />
        </LocalizationProvider>
        <Divider flexItem orientation="vertical" />
        <Stack
          spacing={2}
          sx={{
            width: '200px',
            py: theme.spacing(3),
            px: theme.spacing(2),
            maxHeight: '406px'
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack sx={{ fontWeight: 'bold', color: DARK_GREY }}>Selected Dates</Stack>
            <Stack
              sx={{
                color: theme.palette.action.main,
                fontWeight: 'bold',
                paddingInline: theme.spacing(0.65)
              }}
            >
              {selectedDays.length}
            </Stack>
          </Stack>
          <Stack
            sx={{
              overflowY: 'auto'
            }}
          >
            <TransitionGroup>
              {selectedDays.map((day, idx) => (
                <Collapse key={idx}>
                  <DayItem day={day} removeSelectedDate={onDaySelect} />
                </Collapse>
              ))}
            </TransitionGroup>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default MultipleDatesPicker;
